import React, { useState, useEffect, useRef } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Button,
  Dialog,
  Tooltip,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Tab,
  Tabs,
  Paper,
  ListItemIcon,
  Divider,
  Badge,
  Chip,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Handsontable from "handsontable";
import "handsontable/dist/handsontable.full.min.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import CodeIcon from "@mui/icons-material/Code";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { utils } from "xlsx";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import WordViewer from "./WordViewer";
import ImageViewer from "./ImageViewer";
import VideoViewer from "./VideoViewer";
import HTMLViewer from "./HTMLViewer";
import PowerPointViewer from "./PowerPointViewer";
const Documents = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // States for document list and dialogs
  const [documents, setDocuments] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [docName, setDocName] = useState("");
  const [docDescription, setDocDescription] = useState("");
  const [docSortOrder, setDocSortOrder] = useState(0);
  const [docDisabled, setDocDisabled] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);

  // Helper function to determine document type icon based on file name
  const getDocumentIcon = (fileName) => {
    if (!fileName) return <InsertDriveFileIcon />;
    
    const extension = fileName.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ color: '#F40F02' }} />;
      case 'docx':
      case 'doc':
        return <DescriptionIcon sx={{ color: '#2B579A' }} />;
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <TableChartIcon sx={{ color: '#217346' }} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'tiff':
        return <ImageIcon sx={{ color: '#FF9800' }} />;
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'wmv':
        return <VideocamIcon sx={{ color: '#FF4081' }} />;
      case 'html':
      case 'htm':
        return <CodeIcon sx={{ color: '#E44D26' }} />;
      case 'pptx':
      case 'ppt':
        return <SlideshowIcon sx={{ color: '#D24726' }} />;
      default:
        return <InsertDriveFileIcon sx={{ color: '#607D8B' }} />;
    }
  };

  // States for file viewing (Excel)
  const [activeSheet, setActiveSheet] = useState(0);
  const [fileData, setFileData] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const toggleListCollapse = () => {
    setListCollapsed(!listCollapsed);
  };
  // State for PDF viewing
  const [pdfFile, setPdfFile] = useState(null);
  
  // State for Word document viewing
  const [wordFile, setWordFile] = useState(null);
  
  // State for Image viewing
  const [imageFile, setImageFile] = useState(null);
  const [imageMimeType, setImageMimeType] = useState("");
  
  // State for Video viewing
  const [videoFile, setVideoFile] = useState(null);
  const [videoMimeType, setVideoMimeType] = useState("");
  
  // State for HTML viewing
  const [htmlFile, setHtmlFile] = useState(null);
  
  // State for PowerPoint viewing
  const [pptFile, setPptFile] = useState(null);
  const [pptFileName, setPptFileName] = useState("");

  // Refs and token
  const spreadsheetRef = useRef(null);
  const token = localStorage.getItem("token");

  // Create PDF default layout plugin instance
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Fetch document list from API
  const fetchDocuments = () => {
    apiFetch(`/api/documents`)
      .then((res) => res.json())
      .then((data) => setDocuments(data))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    fetchDocuments();
  }, [token]);

  // Handle Upload Document (metadata and file)
  const handleUploadDocument = (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    if (!docFile) {
      setErrorMsg("Please select a file.");
      return;
    }
    const formData = new FormData();
    formData.append("document", docFile);
    formData.append("name", docName);
    formData.append("description", docDescription);
    formData.append("disabled", docDisabled ? 1 : 0);
    formData.append("sort_order", docSortOrder);

    apiFetch(`/api/documents/upload`, {
      method: "POST",
      headers: { 
        // Don't set Content-Type here, let the browser set it correctly for FormData
        "Authorization": `Bearer ${token}` 
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        setSuccessMsg(data.message);
        setUploadDialogOpen(false);
        setDocFile(null);
        setDocName("");
        setDocDescription("");
        setDocSortOrder(0);
        setDocDisabled(false);
        fetchDocuments();
      })
      .catch((err) => setErrorMsg(err.message));
  };

  // Handle viewing a document: Detect file type and load appropriate viewer
  const handleViewDocument = async (docId) => {
    try {
      const response = await apiFetch(
        `/api/documents/${docId}?token=${token}`
      );
      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();
      // Reset previous viewers
      setFileData(null);
      setSheetNames([]);
      setPdfFile(null);
      setWordFile(null);
      setImageFile(null);
      setImageMimeType("");
      setVideoFile(null);
      setVideoMimeType("");
      setHtmlFile(null);
      setPptFile(null);
      setPptFileName("");

      const mimeType = blob.type;
      console.log("Document MIME type:", mimeType);
      
      // Check file name extension for cases where MIME type might be incorrect
      const fileName = documents.find(doc => doc.id === docId)?.name || '';
      const fileExtension = fileName.toLowerCase().split('.').pop();
      
      if (
        mimeType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        // Excel file
        const reader = new FileReader();
        reader.onload = async (e) => {
          const XLSX = await import("xlsx");
          const binaryStr = e.target.result;
          const workbook = XLSX.read(binaryStr, { type: "binary" });
          const sheetNames = workbook.SheetNames;
          setSheetNames(sheetNames);
          const sheetData = sheetNames.map((sheetName) => {
            const sheet = workbook.Sheets[sheetName];
            return utils.sheet_to_json(sheet, { header: 1 });
          });
          setFileData(sheetData);
          setActiveSheet(0);
          // Slight delay to ensure state updates before initializing Handsontable
          setTimeout(() => {
            loadSheetData(0, sheetData);
          }, 150);
        };
        reader.readAsBinaryString(blob);
      } else if (mimeType === "application/pdf") {
        // PDF file
        const fileURL = URL.createObjectURL(blob);
        setPdfFile(fileURL);
      } else if (
        mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        mimeType === "application/msword"
      ) {
        // Word document (.docx or .doc)
        const arrayBuffer = await blob.arrayBuffer();
        setWordFile(arrayBuffer);
      } else if (
        mimeType === "image/png" ||
        mimeType === "image/jpeg" ||
        mimeType === "image/jpg" ||
        mimeType === "image/tiff"
      ) {
        // Image file (PNG, JPEG, TIFF)
        const fileURL = URL.createObjectURL(blob);
        setImageFile(fileURL);
        setImageMimeType(mimeType);
      } else if (
        mimeType === "video/quicktime" ||
        mimeType === "video/mp4" ||
        mimeType === "video/mov" ||
        // Handle .mov files that might be misidentified by MIME type
        fileExtension === "mov"
      ) {
        // Video file (MOV)
        const fileURL = URL.createObjectURL(blob);
        setVideoFile(fileURL);
        setVideoMimeType(mimeType || "video/quicktime"); // Default to video/quicktime if MIME not detected
      } else if (
        mimeType === "text/html" ||
        fileExtension === "html" ||
        fileExtension === "htm"
      ) {
        // HTML file
        const fileURL = URL.createObjectURL(blob);
        setHtmlFile(fileURL);
      } else if (
        mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        mimeType === "application/vnd.ms-powerpoint" ||
        fileExtension === "pptx" ||
        fileExtension === "ppt"
      ) {
        // PowerPoint file (.pptx or .ppt)
        const arrayBuffer = await blob.arrayBuffer();
        setPptFile(arrayBuffer);
        setPptFileName(fileName);
      } else {
        console.error("Unsupported file type:", mimeType, "Extension:", fileExtension);
      }
    } catch (error) {
      console.error("Error opening file:", error);
    }
  };

  // Load Excel sheet data into Handsontable
  const loadSheetData = (sheetIndex, sheetData) => {
    if (!spreadsheetRef.current) {
      console.error("Spreadsheet ref is null.");
      return;
    }
    const sheet = sheetData[sheetIndex];
    
    // Add debug to check the sheet data
    console.log("Loading sheet data:", sheet ? `${sheet.length} rows` : "No data");
    
    // Destroy any previous Handsontable instance
    spreadsheetRef.current.innerHTML = "";
    
    // Detect if user is on iOS device
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    console.log("Device detection - iOS:", isIOS);
    
    // Function to detect which columns likely contain Excel date values
    const detectDateColumns = (data) => {
      if (!data || data.length < 2) return []; // Need at least headers and one data row
      
      const dateColumns = [];
      const headerRow = data[0];
      
      // Skip the header row (index 0)
      for (let col = 0; col < headerRow.length; col++) {
        let dateCount = 0;
        let totalNonEmptyValues = 0;
        
        // Check values in this column, starting from row 1 (skipping header)
        for (let row = 1; row < Math.min(data.length, 20); row++) { // Check up to 20 rows for efficiency
          if (data[row] && data[row][col] !== undefined && data[row][col] !== null && data[row][col] !== '') {
            totalNonEmptyValues++;
            
            // Check if the value looks like an Excel date number
            // Excel dates are typically numbers between ~25000 (1970) and ~50000 (2025+)
            const value = data[row][col];
            if (typeof value === 'number' && value > 25000 && value < 50000) {
              dateCount++;
            }
          }
        }
        
        // If more than 60% of non-empty values in this column look like dates, mark it as a date column
        if (totalNonEmptyValues > 0 && (dateCount / totalNonEmptyValues > 0.6)) {
          dateColumns.push(col);
        }
      }
      
      return dateColumns;
    };
    
    // Detect date columns in the sheet
    const dateColumns = detectDateColumns(sheet);
    console.log("Detected date columns:", dateColumns);
    
    // Create column configuration with date formatting for detected date columns
    const columnsConfig = [];
    if (sheet && sheet[0]) {
      for (let i = 0; i < sheet[0].length; i++) {
        if (dateColumns.includes(i)) {
          columnsConfig.push({
            type: 'date',
            dateFormat: 'MM/DD/YYYY',
            correctFormat: true,
            // Function to properly format Excel dates
            renderer: function(instance, td, row, col, prop, value, cellProperties) {
              if (value !== null && value !== undefined && typeof value === 'number') {
                try {
                  // Convert Excel date number to JavaScript Date
                  // Excel date system: days since Dec 30, 1899 (with special handling for 1900 leap year bug)
                  let excelDate = value;
                  
                  // Adjust for Excel's 1900 leap year bug (Excel incorrectly treats 1900 as a leap year)
                  if (excelDate > 59) { // Dates after February 28, 1900
                    excelDate -= 1;
                  }
                  
                  // Convert Excel date to JavaScript date
                  // 25569 is the number of days between Excel's start date (1/1/1900) and JavaScript's (1/1/1970)
                  const date = new Date((excelDate - 25569) * 86400 * 1000);
                  
                  // Format date as MM/DD/YYYY
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const year = date.getFullYear();
                  
                  td.innerHTML = `${month}/${day}/${year}`;
                } catch (e) {
                  // Fallback to displaying the raw value if conversion fails
                  td.innerHTML = value;
                }
              } else {
                td.innerHTML = value;
              }
              return td;
            }
          });
        } else {
          // Use default configuration for non-date columns
          columnsConfig.push({});
        }
      }
    }
    
    // Initialize with essential features: copy/paste functionality, context menu, and column resize
    const hot = new Handsontable(spreadsheetRef.current, {
      data: sheet,
      colHeaders: sheet[0] || [],
      columns: columnsConfig,
      rowHeaders: true,
      filters: true,
      columnSorting: true,
      dropdownMenu: true,
      autoWrapRow: true,
      copyable: true,                // Enable copy to clipboard functionality
      contextMenu: true,             // Enable right-click menu with copy/paste options
      manualColumnResize: true,      // Allow column resizing
      licenseKey: "non-commercial-and-evaluation",
      
      // Improved mobile support
      outsideClickDeselects: false,  // Prevents deselection when clicking outside
      fragmentSelection: true,       // Better selection handling for touch devices
      fillHandle: {
        autoInsertRow: false,        // Disable auto-insert to prevent accidental data insertion
        direction: 'vertical',       // Allow vertical fill direction only
        touchEnabled: true           // Enable touch support for fill handle
      },
      
      // Better touch interaction support
      touchScroll: true,             // Enable touch scrolling
      enterBeginsEditing: false,     // Don't automatically begin editing on enter (better for mobile)
    });
    
      // Add mobile copy support with special handling for iOS
      if (isIOS || isMobile) {
        // First, thoroughly remove any existing "View & Copy Data" buttons anywhere in the document
        // This ensures we don't accumulate duplicate buttons when switching between custom tabs
        const existingButtons = document.querySelectorAll('.handson-copy-data-btn');
        existingButtons.forEach(button => {
          if (button && button.parentNode) {
            button.parentNode.removeChild(button);
          }
        });
        
        // Now create and add the new button with a specific class name
        const mobileCopyButton = document.createElement('button');
        mobileCopyButton.innerText = 'View & Copy Data';
        mobileCopyButton.className = 'handson-copy-data-btn'; // Specific class for easier identification and removal
        mobileCopyButton.style.padding = '8px 16px';
        mobileCopyButton.style.backgroundColor = '#1976d2';
        mobileCopyButton.style.color = 'white';
        mobileCopyButton.style.border = 'none';
        mobileCopyButton.style.borderRadius = '4px';
        mobileCopyButton.style.margin = '10px 0';
        mobileCopyButton.style.fontSize = '16px';
        mobileCopyButton.style.fontWeight = 'bold';
        mobileCopyButton.style.width = '100%';
        mobileCopyButton.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        
        // Insert button before the spreadsheet container
        spreadsheetRef.current.parentNode.insertBefore(mobileCopyButton, spreadsheetRef.current);
      
      // When the button is clicked, show a modal with the selected data in a format that's easy to copy
      mobileCopyButton.addEventListener('click', () => {
        const selectedRange = hot.getSelectedRange();
        if (!selectedRange) {
          alert('Please select cells first, then tap "View & Copy Data"');
          return;
        }
        
        // Extract selected cell data
        const selection = selectedRange[0];
        const startRow = selection.from.row;
        const startCol = selection.from.col;
        const endRow = selection.to.row;
        const endCol = selection.to.col;
        
        // Create formatted data
        let tableHtml = '<table style="border-collapse: collapse; width: 100%;">';
        // Add header row if it's included in selection
        if (startRow === 0) {
          tableHtml += '<thead><tr>';
          for (let col = startCol; col <= endCol; col++) {
            tableHtml += `<th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #f2f2f2;">${hot.getDataAtCell(0, col) || ''}</th>`;
          }
          tableHtml += '</tr></thead>';
        }
        
        tableHtml += '<tbody>';
        const firstDataRow = startRow === 0 ? 1 : startRow;
        for (let row = firstDataRow; row <= endRow; row++) {
          tableHtml += '<tr>';
          for (let col = startCol; col <= endCol; col++) {
            tableHtml += `<td style="border: 1px solid #ddd; padding: 8px;">${hot.getDataAtCell(row, col) || ''}</td>`;
          }
          tableHtml += '</tr>';
        }
        tableHtml += '</tbody></table>';
        
        // Prepare plain text version optimized for mobile copying with minimal whitespace
        let plainText = '';
        for (let row = startRow; row <= endRow; row++) {
          const rowData = [];
          for (let col = startCol; col <= endCol; col++) {
            // Get cell data and ensure it's a string, then trim it
            const cellData = hot.getDataAtCell(row, col);
            const cellText = cellData !== null && cellData !== undefined ? String(cellData).trim() : '';
            rowData.push(cellText);
          }
          
          // Remove empty trailing cells
          while (rowData.length > 0 && rowData[rowData.length - 1] === '') {
            rowData.pop();
          }
          
          // Only add non-empty rows
          if (rowData.length > 0) {
            // Use a space instead of tab to minimize whitespace in copied text for iOS
            plainText += rowData.join('  ') + '\n';
          }
        }
        
        // Trim the final result to remove any trailing newlines
        plainText = plainText.trim();
        
        // Create a modal dialog
        const modal = document.createElement('div');
        modal.style.position = 'fixed';
        modal.style.left = '0';
        modal.style.top = '0';
        modal.style.width = '100%';
        modal.style.height = '100%';
        modal.style.backgroundColor = 'rgba(0,0,0,0.7)';
        modal.style.zIndex = '10000';
        modal.style.display = 'flex';
        modal.style.flexDirection = 'column';
        modal.style.alignItems = 'center';
        modal.style.justifyContent = 'center';
        modal.style.padding = '20px';
        
        // Create a content area
        const modalContent = document.createElement('div');
        modalContent.style.backgroundColor = 'white';
        modalContent.style.width = '90%';
        modalContent.style.maxHeight = '80%';
        modalContent.style.borderRadius = '8px';
        modalContent.style.padding = '20px';
        modalContent.style.overflow = 'auto';
        modalContent.style.display = 'flex';
        modalContent.style.flexDirection = 'column';
        
        // Add a title
        const title = document.createElement('h3');
        title.innerText = 'Selected Data';
        title.style.margin = '0 0 15px 0';
        modalContent.appendChild(title);
        
        // Add specific instructions for iOS
        const instructions = document.createElement('div');
        instructions.style.margin = '0 0 15px 0';
        instructions.style.padding = '10px';
        instructions.style.backgroundColor = '#f8f9fa';
        instructions.style.borderRadius = '4px';
        instructions.style.fontSize = '14px';
        instructions.style.lineHeight = '1.4';
        
        if (isIOS) {
          instructions.innerHTML = '<strong>iOS Instructions:</strong><br>' +
            '1. Press and hold on the text below<br>' +
            '2. Use the handles to select all content<br>' +
            '3. Tap "Copy" in the menu that appears<br>' +
            '4. Now you can paste it elsewhere';
        } else {
          instructions.innerHTML = '<strong>Copy Instructions:</strong><br>' +
            '1. Tap inside the text area below<br>' +
            '2. Select all (tap the Select All button)<br>' +
            '3. Copy the content<br>' +
            '4. Close this window when done';
        }
        
        modalContent.appendChild(instructions);
        
        // Create a close button
        const closeButton = document.createElement('button');
        closeButton.innerText = 'Close';
        closeButton.style.padding = '8px 16px';
        closeButton.style.backgroundColor = '#f44336';
        closeButton.style.color = 'white';
        closeButton.style.border = 'none';
        closeButton.style.borderRadius = '4px';
        closeButton.style.marginTop = '15px';
        closeButton.style.alignSelf = 'center';
        closeButton.style.fontSize = '16px';
        
        // Create a proper textarea for iOS
        const textAreaContainer = document.createElement('div');
        textAreaContainer.style.marginBottom = '15px';
        textAreaContainer.style.width = '100%';
        
        // Create a container for the text display
        const textDisplayContainer = document.createElement('div');
        textDisplayContainer.style.position = 'relative';
        textDisplayContainer.style.width = '100%';
        textDisplayContainer.style.marginBottom = '15px';
        
        // Create an optimized textarea for mobile copying
        const textArea = document.createElement('textarea');
        textArea.value = plainText;
        textArea.style.width = '100%';
        textArea.style.height = '150px';
        textArea.style.padding = '15px';
        textArea.style.border = '1px solid #ccc';
        textArea.style.borderRadius = '4px';
        textArea.style.backgroundColor = '#f5f5f5';
        textArea.style.fontFamily = 'monospace';
        textArea.style.fontSize = '16px'; 
        textArea.style.marginBottom = '0';
        textArea.style.whiteSpace = 'pre-wrap'; // Allow wrapping for clean display
        textArea.style.overflowWrap = 'break-word';
        textArea.style.resize = 'none';
        textArea.style.lineHeight = '1.5'; // Better spacing between lines
        textArea.readOnly = true; // Make readonly but still selectable
        textArea.wrap = 'on'; // Enable wrapping to prevent horizontal scrolling
        
        // Add specific iOS copy instructions as a highlighted note
        const copyInstructions = document.createElement('div');
        copyInstructions.style.marginBottom = '15px';
        copyInstructions.style.padding = '10px';
        copyInstructions.style.backgroundColor = '#fff3cd';
        copyInstructions.style.border = '1px solid #ffeeba';
        copyInstructions.style.borderRadius = '4px';
        copyInstructions.style.color = '#856404';
        copyInstructions.style.fontWeight = 'bold';
        copyInstructions.style.textAlign = 'center';
        
        if (isIOS) {
          copyInstructions.innerHTML = '👇 Tap inside the box, then tap again and hold to see the "Copy" option 👇';
        } else {
          copyInstructions.innerHTML = 'Select text and use Ctrl+C or right-click to copy';
        }
        
        // Create a button row
        const buttonRow = document.createElement('div');
        buttonRow.style.display = 'flex';
        buttonRow.style.justifyContent = 'space-between';
        buttonRow.style.marginBottom = '15px';
        buttonRow.style.gap = '10px';
        
        // Add a Select All button
        const selectAllButton = document.createElement('button');
        selectAllButton.innerText = 'Select All Text';
        selectAllButton.style.flex = '1';
        selectAllButton.style.padding = '12px 16px';
        selectAllButton.style.backgroundColor = '#4CAF50';
        selectAllButton.style.color = 'white';
        selectAllButton.style.border = 'none';
        selectAllButton.style.borderRadius = '4px';
        selectAllButton.style.fontSize = '16px';
        selectAllButton.style.fontWeight = 'bold';
        
        // Add a direct Copy button (uses modern Clipboard API with fallbacks)
        const copyButton = document.createElement('button');
        copyButton.innerText = 'Copy to Clipboard';
        copyButton.style.flex = '1';
        copyButton.style.padding = '12px 16px';
        copyButton.style.backgroundColor = '#007bff';
        copyButton.style.color = 'white';
        copyButton.style.border = 'none';
        copyButton.style.borderRadius = '4px';
        copyButton.style.fontSize = '16px';
        copyButton.style.fontWeight = 'bold';
        
        // Custom select all function that avoids selecting trailing whitespace
        const customSelectAll = () => {
          textArea.focus();
          
          // For most browsers, we can be precise about the selection
          if (!isIOS) {
            // Standard selection
            textArea.setSelectionRange(0, plainText.length);
          } else {
            // For iOS, we use a more compatible approach
            textArea.select();
          }
          
          if (isIOS) {
            // Show a hint for iOS users about what to do next
            copyInstructions.innerHTML = '👆 Now tap "Copy to Clipboard" or long-press and select "Copy" 👆';
            copyInstructions.style.backgroundColor = '#d4edda';
            copyInstructions.style.borderColor = '#c3e6cb';
            copyInstructions.style.color = '#155724';
          }
        };
        
        // Select all button functionality using our custom function
        selectAllButton.addEventListener('click', customSelectAll);
        
        // Copy button functionality (with fallbacks for iOS)
        copyButton.addEventListener('click', () => {
          textArea.focus();
          textArea.select();
          
          let copied = false;
          
          // Try to use the Clipboard API (modern browsers)
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(plainText)
              .then(() => {
                showCopySuccess();
              })
              .catch(err => {
                console.error('Clipboard API failed:', err);
                fallbackCopy();
              });
          } else {
            fallbackCopy();
          }
          
          function fallbackCopy() {
            try {
              const successful = document.execCommand('copy');
              if (successful) {
                showCopySuccess();
              } else if (isIOS) {
                // Special instructions for iOS if execCommand fails
                copyInstructions.innerHTML = 'Please try long-pressing the text and select "Copy" from the menu';
                copyInstructions.style.backgroundColor = '#fff3cd';
                copyInstructions.style.borderColor = '#ffeeba';
                copyInstructions.style.color = '#856404';
              }
            } catch (err) {
              console.error('fallback copy failed:', err);
              if (isIOS) {
                alert("To copy: Select text, then long press and tap 'Copy'");
              }
            }
          }
          
          function showCopySuccess() {
            copyButton.innerText = '✓ Copied!';
            copyButton.style.backgroundColor = '#28a745';
            copyInstructions.innerHTML = 'Text copied successfully!';
            copyInstructions.style.backgroundColor = '#d4edda';
            copyInstructions.style.borderColor = '#c3e6cb';
            copyInstructions.style.color = '#155724';
            
            setTimeout(() => {
              copyButton.innerText = 'Copy to Clipboard';
              copyButton.style.backgroundColor = '#007bff';
            }, 2000);
          }
        });
        
        // For iOS specifically, add a touchstart listener to ensure text selection works
        if (isIOS) {
          textArea.addEventListener('touchstart', function() {
            // This empty handler ensures iOS recognizes the element as interactive
          });
          
          // Force the text to be editable for a brief moment when tapped
          textArea.addEventListener('touchend', function(e) {
            // iOS needs a slight delay to register the tap properly
            setTimeout(function() {
              textArea.readOnly = false;
              textArea.focus();
              textArea.click();
              // Return to readonly to prevent keyboard from showing
              setTimeout(function() {
                textArea.readOnly = true;
              }, 100);
            }, 50);
          });
        }
        
        // Add elements to the modal
        modalContent.appendChild(copyInstructions);
        textDisplayContainer.appendChild(textArea);
        modalContent.appendChild(textDisplayContainer);
        buttonRow.appendChild(selectAllButton);
        buttonRow.appendChild(copyButton);
        modalContent.appendChild(buttonRow);
        modalContent.appendChild(closeButton);
        modal.appendChild(modalContent);
        document.body.appendChild(modal);
        
        // Close the modal when the close button is clicked
        closeButton.addEventListener('click', () => {
          document.body.removeChild(modal);
        });
        
        // Auto focus the text area for immediate access
        setTimeout(() => {
          if (!isIOS) {
            textArea.focus();
          }
        }, 300);
      });
      
      // Cleanup function
      return () => {
        if (spreadsheetRef.current && spreadsheetRef.current.parentNode) {
          if (spreadsheetRef.current.parentNode.contains(mobileCopyButton)) {
            spreadsheetRef.current.parentNode.removeChild(mobileCopyButton);
          }
        }
      };
    }
  };

  // Handle sheet tab change for Excel files
  const handleTabChange = (event, newValue) => {
    setActiveSheet(newValue);
    loadSheetData(newValue, fileData);
  };

  // Handle editing document metadata (not content)
  const handleEditClick = (doc) => {
    setEditDoc(doc);
    // Pre-fill edit fields with document metadata
    setDocName(doc.name);
    setDocDescription(doc.description);
    setDocSortOrder(doc.sort_order);
    setDocDisabled(doc.disabled === 1);
    setEditDialogOpen(true);
  };

  const handleEditSave = () => {
    if (!editDoc) return;
    const updatedDoc = {
      name: docName,
      description: docDescription,
      disabled: docDisabled ? 1 : 0,
      sort_order: docSortOrder,
    };

    apiFetch(`/api/documents/${editDoc.id}`, {
      method: "PUT",
      body: JSON.stringify(updatedDoc),
    })
      .then((res) => res.json())
      .then((data) => {
        setSuccessMsg(data.message);
        setEditDialogOpen(false);
        setEditDoc(null);
        fetchDocuments();
      })
      .catch((err) => setErrorMsg(err.message));
  };

  // Handle deleting a document
  const handleDeleteDocument = (docId) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      apiFetch(`/api/documents/${docId}`, {
        method: "DELETE",
      })
        .then((res) => res.json())
        .then((data) => {
          setSuccessMsg(data.message);
          fetchDocuments();
        })
        .catch((err) => setErrorMsg(err.message));
    }
  };

  return (
    <Box sx={{ p: 0, px: 1 }}>
      {/* Main content area - responsive layout based on device */}
      <Box sx={{ 
        display: "flex", 
        flexDirection: isMobile ? "column" : "row", 
        gap: 2, 
        position: "relative", 
        mt: 0 
      }}>
        {/* Document List - positioned at top for mobile, left for desktop */}
          <Paper 
          elevation={3}
          sx={{ 
            width: isMobile 
              ? "100%" 
              : (listCollapsed ? "64px" : "272px"), 
            maxHeight: isMobile 
              ? "325px" 
              : "calc(100vh - 80px)",
            overflow: "auto",
            transition: 'width 0.3s ease-in-out',
            position: 'relative',
            borderRadius: 2,
            bgcolor: '#f8f9fa'
          }}
        >
          <Box sx={{
            position: 'sticky',
            top: 0,
            bgcolor: '#1976d2',
            color: 'white',
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 2,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}>
            {/* Title and collapse button row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}>
              {!listCollapsed && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Documents
                </Typography>
              )}
              <IconButton
                onClick={toggleListCollapse}
                size="small"
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  padding: 0.8,
                  ml: listCollapsed ? 0 : 'auto',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}
              >
                {listCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Box>
            
            {/* Subtle scroll hint - Only show for mobile and when list is not collapsed */}
            {isMobile && !listCollapsed && documents.length > 5 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.65rem', 
                  opacity: 0.85,
                  alignSelf: 'center',
                  mt: -0.2,
                  mb: -0.5,
                  letterSpacing: '0.03em',
                  fontWeight: 400
                }}
              >
                scroll down for more
              </Typography>
            )}
          </Box>
          
          {isMobile && !listCollapsed ? (
            // Single-column scrollable list for mobile
            <Box sx={{ position: 'relative' }}>
              <List sx={{ p: 0 }}>
                {documents.map((doc) => (
                  <Box key={doc.id}>
                    <ListItemButton
                      disableGutters
                      onClick={() => {
                        handleViewDocument(doc.id);
                        setSelectedDocId(doc.id);
                      }}
                      sx={{
                        py: 0.8,
                        px: 1.5,
                        backgroundColor: selectedDocId === doc.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        borderLeft: selectedDocId === doc.id ? '4px solid #1976d2' : '4px solid transparent',
                        '&:hover': {
                          backgroundColor: selectedDocId === doc.id ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getDocumentIcon(doc.name)}
                      </ListItemIcon>
                      
                      <Tooltip 
                        title={doc.description || 'No description available'} 
                        placement="top"
                        enterDelay={500}
                      >
                        <ListItemText 
                          primary={doc.name}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { 
                              fontWeight: selectedDocId === doc.id ? 600 : 500,
                              fontSize: '0.9rem',
                              lineHeight: 1.2,
                              color: '#1976d2',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }
                          }}
                        />
                      </Tooltip>
                      
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(doc);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                      </Box>
                    </ListItemButton>
                    <Divider component="li" sx={{ height: '1px', opacity: 0.4 }} />
                  </Box>
                ))}
              </List>
              
              {/* All scroll indicators removed as requested */}
            </Box>
          ) : (
            // Standard list layout for desktop or collapsed mobile view
            <List sx={{ 
              p: 0,
              '& .MuiListItem-root': { 
                transition: 'all 0.2s ease',
              }
            }}>
              {documents.map((doc) => (
                <Box key={doc.id}>
                  <ListItemButton
                    disableGutters
                    onClick={() => {
                      handleViewDocument(doc.id);
                      setSelectedDocId(doc.id);
                    }}
                    sx={{
                      py: 0.6,
                      px: 1,
                      backgroundColor: selectedDocId === doc.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      borderLeft: selectedDocId === doc.id ? '4px solid #1976d2' : '4px solid transparent',
                      '&:hover': {
                        backgroundColor: selectedDocId === doc.id ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                  
                  {!listCollapsed && (
                    <Tooltip 
                      title={doc.description || 'No description available'} 
                      placement="top"
                      enterDelay={500}
                    >
                      <ListItemText 
                        primary={doc.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { 
                            fontWeight: selectedDocId === doc.id ? 600 : 500,
                            fontSize: '0.8rem',
                            lineHeight: 1.2,
                            color: '#1976d2', // Always use link color
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pl: 0.5,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                  
                  {!listCollapsed && (
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(doc);
                        }}
                        sx={{ ml: 0.5, p: 0.25 }}
                      >
                        <EditIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Box>
                  )}
                </ListItemButton>
                {!listCollapsed && <Divider component="li" sx={{ height: '1px', opacity: 0.4 }} />}
              </Box>
            ))}
          </List>
          )}
        </Paper>

        {/* Right side content area */}
        <Box sx={{ flexGrow: 1, width: isMobile ? '100%' : 'auto' }}>
          {/* Button in upper right corner - absolute positioned */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setUploadDialogOpen(true)}
              sx={{ mr: 1 }}
            >
              Load New Document
            </Button>
          </Box>
          {/* Alerts shown if needed */}
          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
          {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
        {fileData && sheetNames.length > 0 && (
          <>
            <Tabs
              value={activeSheet}
              onChange={handleTabChange}
              aria-label="Excel Sheets"
              variant="scrollable"
              scrollButtons="auto"
            >
              {sheetNames.map((sheetName) => (
                <Tab key={sheetName} label={sheetName} />
              ))}
            </Tabs>
            <Box sx={{ mt: 2, border: "1px solid #ccc", height: "500px", overflow: "auto" }}>
              <div ref={spreadsheetRef} />
            </Box>
          </>
        )}
        {pdfFile && (
          <Box sx={{ mt: 2, border: "1px solid #ccc", height: "500px", overflow: "auto" }}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer fileUrl={pdfFile} plugins={[defaultLayoutPluginInstance]} />
            </Worker>
          </Box>
        )}
        {wordFile && (
          <Box sx={{ mt: 2 }}>
            <WordViewer arrayBuffer={wordFile} />
          </Box>
        )}
        {imageFile && (
          <Box sx={{ mt: 2 }}>
            <ImageViewer imageUrl={imageFile} mimeType={imageMimeType} />
          </Box>
        )}
        {videoFile && (
          <Box sx={{ mt: 2 }}>
            <VideoViewer videoUrl={videoFile} mimeType={videoMimeType} />
          </Box>
        )}
        {htmlFile && (
          <Box sx={{ mt: 2 }}>
            <HTMLViewer htmlUrl={htmlFile} />
          </Box>
        )}
        {pptFile && (
          <Box sx={{ mt: 2 }}>
            <PowerPointViewer 
              arrayBuffer={pptFile} 
              fileName={pptFileName} 
              documentId={documents.find(doc => doc.name === pptFileName)?.id} 
            />
          </Box>
        )}
        </Box>
      </Box>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload New Document</DialogTitle>
        <DialogContent>
          <TextField
            label="Document Name"
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={docDescription}
            onChange={(e) => setDocDescription(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Sort Order"
            type="number"
            value={docSortOrder}
            onChange={(e) => setDocSortOrder(e.target.value)}
            fullWidth
            margin="normal"
          />
          <FormControlLabel
            control={<Checkbox checked={docDisabled} onChange={(e) => setDocDisabled(e.target.checked)} />}
            label="Disabled"
          />
          <Box sx={{ mt: 2 }}>
            <input type="file" onChange={(e) => setDocFile(e.target.files[0])} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUploadDocument} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <TextField
            label="Document Name"
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={docDescription}
            onChange={(e) => setDocDescription(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Sort Order"
            type="number"
            value={docSortOrder}
            onChange={(e) => setDocSortOrder(e.target.value)}
            fullWidth
            margin="normal"
          />
          <FormControlLabel
            control={<Checkbox checked={docDisabled} onChange={(e) => setDocDisabled(e.target.checked)} />}
            label="Disabled"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => handleDeleteDocument(editDoc?.id)} 
            color="error" 
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documents;
